import { Button } from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useRootStore } from 'store/rootStore';
import { useHistory, useParams } from 'react-router-dom';
import { USER_ACTIONS } from 'types/enrollmentService';
import { toJS } from 'mobx';
import ActionModal from 'components/common/modals/ActionModal';
import { ModalType } from 'global/utils/ModalsHelper';
import { ProposalPageType, useRFQProposalPageType } from 'global/hooks/useRFQProposalPageType';
import { getRFQProposalChecklistURL } from 'global/checklists-helpers/RFQProposalChecklistHelper';
import { createStyles } from './styles';

interface FooterProps {
  type: ProposalPageType;
  onCompleteStep?: () => void;
  openWarningModal?: () => void;
}

interface RFQButtonsWrapperProps {
  completeButtonText?: string;
  isCompleteButtonDisabled?: boolean;
  onAction?: () => void;
}

const Footer = observer(
  ({ completeButtonText, onAction, isCompleteButtonDisabled = false }: RFQButtonsWrapperProps): JSX.Element => {
    const classes = createStyles();
    const history = useHistory();
    const { periodId } = useParams<{ periodId: string }>();

    // Mobx store
    const {
      proposalItemsStore: { cart },
    } = useRootStore();

    const onExit = (): void => {
      const rfqChecklistUrl = getRFQProposalChecklistURL({ periodId, cartId: cart.id });
      history.push(rfqChecklistUrl);
    };

    return (
      <footer className={classes.footer}>
        <Button size="large" type="outlined" onClick={onExit} className={classes.exitButton}>
          Back
        </Button>
        {completeButtonText && onAction && (
          <Button size="large" color="primary" onClick={onAction} disabled={isCompleteButtonDisabled}>
            {completeButtonText}
          </Button>
        )}
      </footer>
    );
  },
);

const FooterByType = observer(({ type, onCompleteStep, openWarningModal }: FooterProps): JSX.Element | null => {
  // Mobx store
  const {
    proposalItemsStore: { areAllPortfoliosWithRates },
  } = useRootStore();

  switch (type) {
    case 'CREATE_PORTFOLIO':
      return <Footer completeButtonText="Complete" onAction={onCompleteStep} />;
    case 'RATE':
      return (
        <Footer
          completeButtonText="Submit Rates"
          onAction={onCompleteStep}
          isCompleteButtonDisabled={!areAllPortfoliosWithRates}
        />
      );
    case 'FILE_UPLOAD':
      return <Footer completeButtonText="Submit Upload" onAction={onCompleteStep} />;
    case 'RELEASE':
      return <Footer completeButtonText="Release" onAction={openWarningModal} />;
    case 'GENERAL':
      return <Footer />;
    default:
      return null;
  }
});

const RFQProposalPageFooter: React.FC = observer(() => {
  const history = useHistory();
  const pageType = useRFQProposalPageType();
  const { periodId } = useParams<{ periodId: string }>();
  const [showWarningModal, setShowWarningModal] = useState(false);

  function openWarningModal(): void {
    setShowWarningModal(true);
  }

  function closeWarningModal(): void {
    setShowWarningModal(false);
  }

  // Mobx store
  const {
    proposalItemsStore: { cart, completeStep },
  } = useRootStore();

  const convertedCart = toJS(cart);

  const onCompleteStep = async (): Promise<void> => {
    const getUserAction = (): USER_ACTIONS => {
      switch (pageType) {
        case 'CREATE_PORTFOLIO':
          return USER_ACTIONS.COMPLETE_CREATE_PORTFOLIO;
        case 'RATE':
          return USER_ACTIONS.COMPLETE_RATE_RFQ;
        case 'FILE_UPLOAD':
          return USER_ACTIONS.COMPLETE_ADD_COLLATERAL;
        case 'RELEASE':
          return USER_ACTIONS.COMPLETE_RELEASE;
        // This action should not be triggered. It is used to save return type
        default:
          return USER_ACTIONS.COMPLETE_PROPOSAL;
      }
    };

    await completeStep(periodId || '', convertedCart.id, getUserAction());
    if (pageType === 'RELEASE') {
      history.push(`/group/${convertedCart.groupId}`);
    } else {
      const rfqChecklistUrl = getRFQProposalChecklistURL({
        periodId: periodId || '',
        cartId: convertedCart.id,
      });
      history.push(rfqChecklistUrl);
    }
  };

  const onCompleteReleaseStep = (): void => {
    closeWarningModal();
    void onCompleteStep();
  };

  return (
    <>
      <FooterByType type={pageType} onCompleteStep={onCompleteStep} openWarningModal={openWarningModal} />
      <ActionModal
        title="Release RFQ for Review"
        subtitle="You are about to complete custom Quote Checklist"
        modalType={ModalType.NOTIFICATION}
        show={showWarningModal}
        onClose={closeWarningModal}
        onAction={onCompleteReleaseStep}
        actionButtonText="Release"
        closeButtonText="Cancel"
      />
    </>
  );
});

export default RFQProposalPageFooter;
